import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import useTestimonial from '../../stores/useTestimonial';
import Skeleton from '../../components/ui/skeleton';
import { LanguageContext } from '../../contexts/LanguageContext';

interface TestimoniCardProps {
  testimoni_id: number;
  name_testi: string;
  message_testi_id: string;
  photo_testi?: string;
  className?: string;
  date?: Date;
}

function TestimoniCard({
  testimoni_id,
  className = '',
  name_testi,
  message_testi_id,
  photo_testi,
  date,
}: TestimoniCardProps) {
  const formattedDate = date ? format(new Date(date), 'd MMM yyyy') : 'Not now';
  return (
    <div
      className={` ${className} max-w-96 xl:w-96 md:w-80 w-full hover-shadow bg-neutral-50 flex flex-col gap-4 p-4 shadow-md rounded-lg overflow-hidden`}
    >
      <p className="md:text-sm text-xs break-words eading-relaxed hyphens-auto">&ldquo;{message_testi_id}&rdquo;</p>
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <img src={photo_testi} className="md:w-10 w-8 object-cover rounded-full h-auto" alt="" />
          <div>
            <h2 className="font-medium md:text-base text-sm whitespace-nowrap overflow-hidden text-ellipsis">
              {name_testi}
            </h2>
            <p className="md:text-sm text-xs text-secondNeutral">{formattedDate}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function TestiSection() {
  const { fetchTestimonials, hasFetched, testimonials, fetchLoading } = useTestimonial();
  const containerRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(1);
  const [cardHeight, setCardHeight] = useState(0);
  const { t } = useTranslation();
  const { language } =
    useContext(LanguageContext) ||
    (() => {
      throw new Error('LanguageSwitcher must be used within a LanguageProvider');
    })();
  const lang = language === 'id';

  useEffect(() => {
    if (!hasFetched) {
      fetchTestimonials();
    }
  }, [fetchTestimonials, hasFetched]);

  useEffect(() => {
    const updateVisibleCount = () => {
      if (!containerRef.current) return;

      const containerHeight = containerRef.current.clientHeight;
      const firstCard = containerRef.current.querySelector('.testi-card');

      if (firstCard) {
        const currentCardHeight = (firstCard as HTMLElement).offsetHeight;
        setCardHeight(currentCardHeight);
        setVisibleCount(Math.floor(containerHeight / currentCardHeight));
      }
    };

    updateVisibleCount();
    window.addEventListener('resize', updateVisibleCount);
    return () => window.removeEventListener('resize', updateVisibleCount);
  }, [testimonials]);

  return (
    <section
      className={
        testimonials.length === 0
          ? 'hidden'
          : 'rounded-lg flex bg-cover p-4 bg-top justify-center space-y-[-50px] md:flex-row flex-col bg-fixed items-center xl:space-x-[-24px] md:space-x-[-70px]'
      }
    >
      <div className="md:h-[640px] h-64 flex flex-col justify-center items-center bg-[url(https://res.cloudinary.com/dwswkz2sk/image/upload/v1740553658/uploads/dmkytpglnillrxphtdab.png)] gap-2 rounded-xl bg-cover bg-center xl:w-[496px] md:w-[396px] w-full  object-cover">
        <h1 className="bg-gradient-to-r from-white to-currency bg-clip-text text-transparent font-bold text-3xl">
          Testimonials
        </h1>
        <p className=" text-white text-xl text-center">
          {t('tagline.home.testi')} <br />
          <i className="font-semibold">MSA Travel Services</i>
        </p>
      </div>
      {fetchLoading ? (
        <div className="h-[540px] overflow-hidden flex flex-col gap-4">
          <Skeleton className="h-24  max-w-96 w-96" />
          <Skeleton className="h-24  max-w-96 w-96" />
          <Skeleton className="h-24  max-w-96 w-96" />
          <Skeleton className="h-24  max-w-96 w-96" />
        </div>
      ) : (
        <div ref={containerRef} className="md:h-[540px] p-2 h-[500px] overflow-hidden items-center flex flex-col gap-4">
          {testimonials.slice(0, visibleCount).map((testi) => (
            <TestimoniCard
              key={testi.testimoni_id}
              name_testi={testi.name_testi}
              message_testi_id={lang ? testi.message_testi_id : testi.message_testi_en || testi.message_testi_id}
              photo_testi={testi.photo_testi || '/assets/no-data-image.svg'}
              testimoni_id={testi.testimoni_id}
              date={testi.created_at}
              className="testi-card"
            />
          ))}
        </div>
      )}
    </section>
  );
}
